import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  TextField,
  styled,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import DialogWrapper from '../../../components/core-ui/DialogWrapper'
import { ACTIVITY_TYPES, FIELD_TYPES, TEAM_SUB_TYPES, TEAM_TYPES } from '../../../utility/constants'
import { fieldTemplateService } from '../../../services/fieldTemplateService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminProcessService } from '../../../services/admin/process'
import { listDataService } from '../../../services/listDataService'
import DefaultValueField from './DefaultValueField'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import ConditionalEnableDisableUI from './ConditionalEnableDisableUI'
import FieldConstraintUI from './FieldConstraintUI'
import ParentFieldSetupUI from './ParentFieldSetupUI'
import FieldVisibilityProtectionUI from './FieldVisibilityProtectionUI'
import TableFieldConstraintUI from './TableFieldConstraintUI'
import FieldComputationSetupUI from './FieldComputationSetupUI'
import { userConfig } from '../../../services/userConfigServices'
import { toast } from 'react-toastify'
import ImportFieldSelectionUI from './ImportFieldSelectionUI'
import PivotFieldFilterSetup from './PivotFieldFilterSetup'
import PreventProcessCreationUI from './PreventProcessCreationUI'
import RecurringTableSetup from './RecurringTableSetup'
import AggregationTables from './AggregationTables'

const booleanProps = [
  'is_subfield',
  'is_required',
  'is_assignment_field',
  'is_read_only',
  'is_computed',
  'is_hidden_in_process_dashboard',
  'is_derived_field',
  'is_selectable_table',
  'allow_add_rows',
  'allow_delete_rows',
  'is_imported_field',
  'is_hidden',
  'is_pivot_field',
  'is_one_time_input',
  'is_process_counter',
  'is_lrp_display',
  'is_aggregation_table',
  'is_visible_in_my_tasks_card',
]

const allColumns = [
  'id',
  'field_template_name',
  'data_structure',
  'sequence',
  'description',
  'created_at',
  'modified_at',
  'created_by',
  'modified_by',
  'list_id',
  'page_template_id',
  'is_subfield',
  'is_required',
  'metadata',
  'is_assignment_field',
  'is_read_only',
  'is_computed',
  'is_hidden_in_process_dashboard',
  'is_derived_field',
  'derived_from_field_template_id',
  'is_selectable_table',
  'allow_add_rows',
  'allow_delete_rows',
  'pivot_field_template_id',
  'is_imported_field',
  'imported_from_field_template_id',
  'is_hidden',
  'is_pivot_field',
  'import_from_process_template_id',
  'subfield_column_width_in_rem',
  'field_width_in_rem_for_process_dashboard',
  'is_one_time_input',
  'dynamic_field_type',
  'is_process_counter',
  'num_decimal_places',
  'is_lrp_display',
  'default_value',
  'is_aggregation_table',
  'aggregation_parent_table_field_template_id',
  'is_visible_in_my_tasks_card',
]

const transformFieldPropForAPI = (obj) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    return { ...acc, [key]: booleanProps.includes(key) ? value == 'yes' : value }
  }, {})
}

const transformFieldPropForUI = (obj) => {
  return Object.entries(obj || {}).reduce((acc, [key, value]) => {
    return { ...acc, [key]: booleanProps.includes(key) ? (value ? 'yes' : 'no') : value }
  }, {})
}

const onlyFieldColumns = (obj, keys) => {
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)))
}

const GroupHeader = styled('Box')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'light' ? '#f7adff' : '#f7adff',
}))

const GroupItems = styled('ul')({
  padding: 0,
})

const EditFieldTemplateDialog = ({
  initialFieldTemplate = null,
  importedFieldList = [],
  lists = [],
  ftData = [],
  processTemplateId,
  activityTemplateId,
  activityTemplate,
  setDialogOpen = (status) => {},
  onChangeSave = async (template) => {},
  onFieldDelete = () => {},
}) => {
  const { user } = useAuthContext()
  const [fieldTemplate, setFieldTemplate] = useState(transformFieldPropForUI(initialFieldTemplate))
  const [derivableFieldTemplates, setDerivableFieldTemplate] = useState([])
  const [parentFieldTemplates, setParentFieldTemplates] = useState([])
  const [processes, setProcesses] = useState([])
  const [pivotFields, setPivotFields] = useState([])
  const [importableFields, setImportableFields] = useState([])
  const [selectedListItems, setSelectedListItems] = useState([])
  const [selectedTabValue, setSelectedTabValue] = React.useState('1')
  const [assignmentFieldData, setAssignmentFieldData] = useState({})
  const [tableField, setTableField] = useState(null)
  const [pages, setPages] = useState([])

  useEffect(() => {
    const pagesFromData = ftData.reduce((acc, cur) => {
      const page = acc?.find((it) => it.id == cur.page_template_id)
      if (!page) {
        return [
          ...acc,
          {
            id: cur.page_template_id,
            page_num: cur.page_num,
            page_template_name: cur.page_template_name,
          },
        ]
      }
      return acc
    }, [])

    setPages(pagesFromData)
  }, [ftData])

  useEffect(() => {
    // find the table field for current subfield
    if (fieldTemplate.is_subfield) {
      const tableFieldForSubfield = ftData.find(
        (field) =>
          field.data_structure == FIELD_TYPES.TABLE &&
          field.subfield_template_ids.split(',').includes(String(fieldTemplate.id))
      )
      setTableField(tableFieldForSubfield)

      // force same pivot field as table field for imported fields from
      if (
        fieldTemplate.is_imported_field &&
        fieldTemplate.data_structure != FIELD_TYPES.TABLE &&
        tableFieldForSubfield
      ) {
        setFieldTemplate({
          ...fieldTemplate,
          pivot_field_template_id: tableFieldForSubfield?.pivot_field_template_id || null,
        })
      }
    }
  }, [ftData, initialFieldTemplate])

  const handleChange = (event, newValue) => {
    setSelectedTabValue(newValue)
  }

  useEffect(() => {
    const pivots = ftData.filter((it) => it.is_pivot_field == true)
    setPivotFields(pivots)

    if (fieldTemplate.pivot_field_template_id) {
      const pivotField = ftData.find((it) => it.id == fieldTemplate.pivot_field_template_id)

      if (tableField?.metadata?.linked_process_config?.import_from_linked_process) {
        // filter all importable fields from all list
        const importable = importedFieldList.filter(
          (it) =>
            it.process_template_id == tableField?.metadata?.linked_process_config?.linked_process
        )
        setImportableFields(() => importable)
      } else {
        // filter all importable fields from all list
        const importable = importedFieldList.filter((it) =>
          pivotField?.import_from_process_template_id?.includes(it.process_template_id)
        )
        setImportableFields(() => importable)
      }
    }
  }, [fieldTemplate, importedFieldList, ftData, tableField])

  useEffect(() => {
    // load items for list type field
    if (
      [FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate?.data_structure) &&
      fieldTemplate.list_id
    ) {
      // call api
      ;(async () => {
        const listItems = (await listDataService.getData(user, fieldTemplate?.list_id)).data
        setSelectedListItems(listItems)
      })()
      // get data and set in local state
    }
  }, [user, fieldTemplate])

  useEffect(() => {
    ;(async () => {
      const res = await Promise.all([
        fieldTemplateService.getDerivableFieldsForProcess(user, processTemplateId),
        adminProcessService.getProcesses(user),
        userConfig.getAllAllowedTeamsInProcess(user, processTemplateId, fieldTemplate.id),
      ])

      // add table field name for subfields
      const updatedFields = res[0]?.map((it) => {
        const tf = it.is_subfield ? res[0].find((tf) => tf.id == it.table_field_template_id) : null
        return {
          ...it,
          field_template_name: tf
            ? tf.field_template_name + '/' + it.field_template_name
            : it.field_template_name,
          table_field_template: tf,
        }
      })

      setParentFieldTemplates(updatedFields.filter((it) => it.data_structure === FIELD_TYPES.TABLE))
      setDerivableFieldTemplate(updatedFields.filter((it) => it.id != fieldTemplate.id))
      setProcesses(res[1].filter((it) => it.id != processTemplateId))
      setAssignmentFieldData(res[2])
    })()
  }, [processTemplateId])

  useEffect(() => {
    const pivots = ftData.filter((it) => it.is_pivot_field == true)
    setPivotFields(pivots)
  }, [ftData])

  useEffect(() => {
    if (fieldTemplate.pivot_field_template_id) {
      const pivotField = pivotFields.find((it) => it.id == fieldTemplate.pivot_field_template_id)
      // filter all importable fields from all list
      setImportableFields(
        importedFieldList.filter((it) =>
          pivotField?.import_from_process_template_id?.includes(it.process_template_id)
        )
      )
    }
  }, [fieldTemplate.pivot_field_template_id])

  useEffect(() => {
    setFieldTemplate((prev) => {
      const isNumericType = prev?.data_structure == FIELD_TYPES.NUMBER

      if (
        isNumericType &&
        (prev?.num_decimal_places === undefined || prev?.num_decimal_places === null)
      ) {
        return { ...prev, num_decimal_places: 2 }
      }

      if (
        !isNumericType &&
        prev?.num_decimal_places !== null &&
        prev?.num_decimal_places !== undefined
      ) {
        return { ...prev, num_decimal_places: null }
      }

      return prev
    })
  }, [fieldTemplate?.data_structure])

  const saveChanges = async () => {
    try {
      // for assignment field, team is mandatory field
      if (fieldTemplate.is_assignment_field == 'yes') {
        if (assignmentFieldData?.selection?.length <= 0) {
          throw new Error('Assigned team is mandatory for assignment field.')
        }
      }

      // write logic here
      const finalField = onlyFieldColumns(transformFieldPropForAPI(fieldTemplate), allColumns)
      const result = await onChangeSave(finalField)
      if (result && fieldTemplate.is_assignment_field == 'yes') {
        updateAssignmentFieldTeamMap()
      }
    } catch (error) {
      console.error('Error saving field template changes:', error)
      toast.error(error.message)
    }
  }

  const saveComputationChanges = (field) => {
    const finalField = field // onlyFieldColumns(transformFieldPropForAPI(field), allColumns)
    onChangeSave(finalField)
  }

  const getAssociatedListValues = (fieldTemplate, lists) => {
    if (!fieldTemplate?.list_id || !lists) {
      return undefined
    }
    return lists?.find((it) => fieldTemplate?.list_id == it.id)
  }

  const updateAssignmentFieldTeamMap = async () => {
    try {
      await userConfig.updateAssignmentFieldTeamMap(user, fieldTemplate.id, {
        team_ids: assignmentFieldData.selection,
      })
      toast.success('Selected team(s) assigned to field!')
    } catch (error) {
      console.error('Error updating assignment field team map:', error)
      toast.error(error.message)
    }
  }

  const getAssignedTeams = (assignmentFieldData) => {
    if (assignmentFieldData?.allowed?.length <= 0) return []
    const selection = assignmentFieldData?.selection?.map((it) => {
      const team = assignmentFieldData?.allowed?.find((t) => t.id == it)
      return { ...team, value: it, label: team?.name }
    })
    return selection
  }

  return (
    <DialogWrapper
      fullWidth
      open={true}
      onClose={() => setDialogOpen(false)}
      heading={`${fieldTemplate?.field_template_name} (Field Settings)`}
      actions={
        <>
          {/* <Button
            variant="contained"
            onClick={() => {
              setDialogOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={saveChanges}>
            Save
          </Button> */}
        </>
      }
    >
      <TabContext value={selectedTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="Field Setting Tabs"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="General" value="1" />
            <Tab label="Advance" value="2" />
            <Tab label="Computation" value="3" />
            {activityTemplate.activity_type == ACTIVITY_TYPES.LONG_RUNNING &&
              fieldTemplate.data_structure == FIELD_TYPES.TABLE && (
                <Tab label="Recurring Table" value="4" />
              )}
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ padding: 0, paddingTop: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
            {/* field template name */}
            <TextField
              type="text"
              value={fieldTemplate?.field_template_name}
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              onChange={(e) =>
                setFieldTemplate({ ...fieldTemplate, field_template_name: e.target.value })
              }
              label={`Field Template Name`}
            />
            {/* Field Type */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Choose Field Type</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label={'Choose Field Type'}
                value={fieldTemplate?.data_structure}
                onChange={(e) => {
                  let updatedField = {
                    ...fieldTemplate,
                    list_id: null,
                    default_value: null,
                    data_structure: e.target.value,
                  }

                  if (e.target.value !== FIELD_TYPES.LIST) {
                    setAssignmentFieldData({
                      ...assignmentFieldData,
                      selection: [],
                    })
                    updatedField = {
                      ...updatedField,
                      is_assignment_field: false,
                    }
                  }
                  setFieldTemplate(updatedField)
                }}
              >
                {Object.entries(FIELD_TYPES).map(([k, v], i) => {
                  if (fieldTemplate?.is_subfield == true && v == FIELD_TYPES.TABLE) {
                    return null
                  } else {
                    return (
                      <MenuItem key={i} value={v}>
                        {v}
                      </MenuItem>
                    )
                  }
                })}
              </Select>
            </FormControl>

            {/* Select List */}
            {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate?.data_structure) && (
              <Autocomplete
                size="small"
                options={lists || []}
                value={getAssociatedListValues(fieldTemplate, lists)}
                getOptionLabel={(option) => option.list_name}
                label="Associated List"
                onChange={(_, v) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    list_id: v?.id || null,
                    dynamic_field_type: null,
                  })
                }
                renderInput={(params) => {
                  return <TextField {...params} label="Associated List" />
                }}
              />
            )}

            {/* field template default value */}
            {fieldTemplate.data_structure != FIELD_TYPES.TABLE &&
              (fieldTemplate.is_subfield == 'yes' ? (
                <>
                  {/* <DefaultValueSubfield // Deprecated: Added tabular view for setting default value to subfield.
                  fieldTemplate={fieldTemplate}
                  selectedListItems={selectedListItems}
                  setFieldTemplate={setFieldTemplate}
                /> */}
                </>
              ) : (
                <DefaultValueField
                  fieldTemplate={fieldTemplate}
                  selectedListItems={selectedListItems}
                  setFieldTemplate={setFieldTemplate}
                />
              ))}
            {/* digit after decimal place */}
            {fieldTemplate.data_structure == FIELD_TYPES.NUMBER && (
              <TextField
                type="number"
                value={fieldTemplate?.num_decimal_places ?? 2}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    num_decimal_places:
                      e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                  })
                }
                label={`Digits After Decimal Place`}
              />
            )}

            {/* Required? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Required?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Required?"
                value={fieldTemplate?.is_required}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_required: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>

            {/* {is_visible_in_my_tasks_card} */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Visible in my tasks card?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Visible in my tasks card?"
                value={fieldTemplate?.is_visible_in_my_tasks_card}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_visible_in_my_tasks_card: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>

            {/* Required? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Is Read Only?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Is Read Only?"
                value={fieldTemplate?.is_read_only}
                disabled={fieldTemplate?.is_computed == 'yes'}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_read_only: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/* Assignment Field? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.LIST &&
              fieldTemplate?.is_subfield === 'no' && (
                <FormControl variant="outlined" fullWidth size="small" color="primary">
                  <InputLabel id="demo-simple-select-label">Assignment Field?</InputLabel>
                  <Select
                    size="small"
                    variant="outlined"
                    color="primary"
                    fullWidth={true}
                    label="Assignment Field?"
                    value={fieldTemplate?.is_assignment_field}
                    disabled={false}
                    onChange={(e) => {
                      setFieldTemplate({
                        ...fieldTemplate,
                        is_assignment_field: e.target.value,
                      })

                      if (e.target.value == 'no') {
                        setAssignmentFieldData({ ...assignmentFieldData, selection: [] })
                      }
                    }}
                  >
                    <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                    <MenuItem value={'no'}>{'No'}</MenuItem>
                  </Select>
                </FormControl>
              )}

            {fieldTemplate?.data_structure === FIELD_TYPES.LIST &&
              fieldTemplate?.is_subfield === 'no' &&
              fieldTemplate.is_assignment_field == 'yes' && (
                <Autocomplete
                  size="small"
                  options={
                    assignmentFieldData?.allowed?.map((it) => ({
                      ...it,
                      value: it.id,
                      label: it.name,
                    })) || []
                  }
                  multiple
                  value={getAssignedTeams(assignmentFieldData) || []}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Choose team(s)"
                      color="primary"
                    />
                  )}
                  onChange={(_, v) => {
                    setAssignmentFieldData({
                      ...assignmentFieldData,
                      selection: v?.map((it) => it.value),
                    })
                  }}
                />
              )}
            {/* Selectable Table? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel id="demo-simple-select-label">Selectable Table?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth={true}
                  label="Selectable Table?"
                  value={fieldTemplate?.is_selectable_table}
                  disabled={false}
                  onChange={(e) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_selectable_table: e.target.value,
                    })
                  }}
                >
                  <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                  <MenuItem value={'no'}>{'No'}</MenuItem>
                </Select>
              </FormControl>
            )}
            {/* Allow Add Rows? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <InputLabel id="demo-simple-select-label">Allow Add Rows?</InputLabel>
                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth={true}
                  label="Allow Add Rows?"
                  value={fieldTemplate?.allow_add_rows}
                  disabled={false}
                  onChange={(e) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      allow_add_rows: e.target.value,
                    })
                  }}
                >
                  <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                  <MenuItem value={'no'}>{'No'}</MenuItem>
                </Select>
              </FormControl>
            )}
            {/* Allow Delete Rows? */}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary" h>
                <InputLabel id="demo-simple-select-label">Allow Delete Rows?</InputLabel>

                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth={true}
                  label="Allow Delete Rows?"
                  value={fieldTemplate?.allow_delete_rows}
                  disabled={false}
                  onChange={(e) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      allow_delete_rows: e.target.value,
                    })
                  }}
                >
                  <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                  <MenuItem value={'no'}>{'No'}</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* is_aggregation_table*/}
            {fieldTemplate?.data_structure === FIELD_TYPES.TABLE && (
              <FormControl variant="outlined" fullWidth size="small" color="primary" h>
                <InputLabel id="demo-simple-select-label">is_aggregation_table</InputLabel>

                <Select
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth={true}
                  label="is_aggregation_table?"
                  value={fieldTemplate?.is_aggregation_table}
                  disabled={false}
                  onChange={(e) => {
                    if (e.target.value == 'no') {
                      setFieldTemplate({
                        ...fieldTemplate,
                        aggregation_parent_table_field_template_id: null,
                        is_aggregation_table: e.target.value,
                      })
                    } else {
                      setFieldTemplate({
                        ...fieldTemplate,
                        is_aggregation_table: e.target.value,
                      })
                    }
                  }}
                >
                  <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                  <MenuItem value={'no'}>{'No'}</MenuItem>
                </Select>
              </FormControl>
            )}

            {fieldTemplate?.is_aggregation_table == 'yes' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="select-aggregation_parent_table_field_template_id"
                  options={parentFieldTemplates}
                  size="small"
                  getOptionLabel={(item) => item?.field_template_name}
                  value={
                    parentFieldTemplates.find(
                      (item) =>
                        item.id === fieldTemplate?.aggregation_parent_table_field_template_id
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      aggregation_parent_table_field_template_id: newValue?.id || null,
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select aggregation_parent_table_field_template_id"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}
            {/* Derived Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Derived Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Derived Field?"
                value={fieldTemplate?.is_derived_field}
                disabled={false}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      derived_from_field_template_id: null,
                      is_derived_field: e.target.value,
                    })
                  } else {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_derived_field: e.target.value,
                      is_read_only: e.target.value,
                    })
                  }
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>

            {/* derived from fields */}
            {fieldTemplate?.is_derived_field == 'yes' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="select-derived-field"
                  options={derivableFieldTemplates
                    .filter(
                      (it) =>
                        it.data_structure == fieldTemplate.data_structure &&
                        it.list_id == fieldTemplate.list_id
                    )
                    .sort(
                      (a, b) => -b.activity_template_name.localeCompare(a.activity_template_name)
                    )}
                  groupBy={(option) => option?.activity_template_name}
                  size="small"
                  getOptionLabel={(item) => item?.field_template_name}
                  value={
                    fieldTemplate?.derived_from_field_template_id
                      ?.split(',')
                      ?.map((id) => derivableFieldTemplates.find((it) => it.id == id)) || []
                  }
                  onChange={(_, newValue) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      derived_from_field_template_id: newValue.map((item) => item?.id).join(','),
                    })
                  }}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select derived field"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}

            {/* Pivot Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Pivot Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Pivot Field?"
                value={fieldTemplate?.is_pivot_field}
                disabled={false}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      import_from_process_template_id: null,
                      is_pivot_field: e.target.value,
                    })
                  } else {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_pivot_field: e.target.value,
                    })
                  }
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>

            {/* select all process templates */}
            {fieldTemplate?.is_pivot_field == 'yes' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="select-derived-field"
                  options={processes}
                  size="small"
                  getOptionLabel={(item) => item?.process_template_name}
                  value={
                    fieldTemplate?.import_from_process_template_id
                      ?.split(',')
                      ?.map((id) => processes.find((it) => it.id == id)) || []
                  }
                  onChange={(_, newValue) => {
                    if (newValue.length > 0) {
                      setFieldTemplate({
                        ...fieldTemplate,
                        import_from_process_template_id: newValue.map((item) => item.id).join(','),
                      })
                    } else {
                      setFieldTemplate({
                        ...fieldTemplate,
                        import_from_process_template_id: null,
                      })
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Processes"
                      color="primary"
                    />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                />
              </FormControl>
            ) : null}

            {/* Imported Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Imported Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Imported Field?"
                value={fieldTemplate?.is_imported_field}
                disabled={false}
                onChange={(e) => {
                  if (e.target.value == 'no') {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_imported_field: e.target.value,
                      pivot_field_template_id: null,
                      imported_from_field_template_id: null,
                    })
                  } else {
                    setFieldTemplate({
                      ...fieldTemplate,
                      is_imported_field: e.target.value,
                      is_read_only: e.target.value,
                    })
                  }
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>

            {fieldTemplate?.is_imported_field == 'yes' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="Select Pivot Field"
                  options={pivotFields}
                  size="small"
                  getOptionLabel={(item) => item.field_template_name}
                  value={
                    fieldTemplate.pivot_field_template_id
                      ? ftData.find((it) => it.id == fieldTemplate.pivot_field_template_id)
                      : null
                  }
                  onChange={(_, newValue) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      pivot_field_template_id: newValue?.id || null,
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select Pivot Field "
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}

            {/* select individual fields will be importing from */}
            <ImportFieldSelectionUI
              fieldTemplate={fieldTemplate}
              setFieldTemplate={setFieldTemplate}
              importableFields={importableFields}
              processTemplateId={processTemplateId}
            />

            {/* Hidden Field? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Hidden Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Hidden Field?"
                value={fieldTemplate?.is_hidden}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_hidden: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/* Hidden in Process Dashboard? */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Hidden in Process Dashboard?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Hidden in Process Dashboard?"
                value={fieldTemplate?.is_hidden_in_process_dashboard}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_hidden_in_process_dashboard: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/* one time input */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">One Time Field?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="One Time Field?"
                value={fieldTemplate?.is_one_time_input}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_one_time_input: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/* process counter */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Is process counter?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Process Counter?"
                value={fieldTemplate?.is_process_counter}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_process_counter: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/* lrp display */}
            <FormControl variant="outlined" fullWidth size="small" color="primary">
              <InputLabel id="demo-simple-select-label">Show in Long Running Process?</InputLabel>
              <Select
                size="small"
                variant="outlined"
                color="primary"
                fullWidth={true}
                label="Show in Long Running Process?"
                value={fieldTemplate?.is_lrp_display}
                disabled={false}
                onChange={(e) => {
                  setFieldTemplate({
                    ...fieldTemplate,
                    is_lrp_display: e.target.value,
                  })
                }}
              >
                <MenuItem value={'yes'}>{'Yes'}</MenuItem>
                <MenuItem value={'no'}>{'No'}</MenuItem>
              </Select>
            </FormControl>
            {/*dynamic field type */}
            {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(fieldTemplate.data_structure) ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  id="dynamic_field_type"
                  options={['users', 'teams']}
                  size="small"
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.type}
                  onChange={(_, value) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: { type: value },
                      list_id: null,
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field Type"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}
            {/*dynamic field type - subtypes */}
            {fieldTemplate?.dynamic_field_type?.type == 'teams' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  multiple
                  id="dynamic_field_type"
                  options={Object.values(TEAM_TYPES)}
                  size="small"
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.subtypes}
                  style={{ marginBottom: '.8rem' }}
                  onChange={(_, value) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: { ...fieldTemplate.dynamic_field_type, subtypes: value },
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field SubType"
                      color="primary"
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  id="dynamic_field_supertype"
                  options={Object.values(TEAM_SUB_TYPES)}
                  size="small"
                  getOptionLabel={(option) => option}
                  value={fieldTemplate?.dynamic_field_type?.supertype || []}
                  onChange={(_, value) => {
                    setFieldTemplate({
                      ...fieldTemplate,
                      dynamic_field_type: {
                        ...fieldTemplate.dynamic_field_type,
                        supertype: Array.isArray(value) && value.length > 0 ? value : null,
                      },
                    })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Dynamic Field SuperType"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}
            {/* Page Name */}
            {fieldTemplate.is_subfield == 'no' ? (
              <FormControl variant="outlined" fullWidth size="small" color="primary">
                <Autocomplete
                  placeholder="Page Name"
                  id="field_page"
                  options={pages}
                  size="small"
                  getOptionLabel={(option) => option.page_template_name}
                  value={pages?.find((it) => it.id == fieldTemplate?.page_template_id) || null}
                  onChange={(_, value) =>
                    setFieldTemplate({
                      ...fieldTemplate,
                      page_template_id: value?.id,
                      page_num: value?.page_num,
                      page_template_name: value?.page_template_name,
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Page Name" color="primary" />
                  )}
                />
              </FormControl>
            ) : null}
            {/* subfield_column_width_in_rem */}
            {fieldTemplate.is_subfield == 'yes' ? (
              <TextField
                type="number"
                value={fieldTemplate?.subfield_column_width_in_rem}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    subfield_column_width_in_rem: e.target.value,
                  })
                }
                label={`Column Width (REM)`}
              />
            ) : (
              <TextField
                type="number"
                value={fieldTemplate?.field_width_in_rem_for_process_dashboard}
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                onChange={(e) =>
                  setFieldTemplate({
                    ...fieldTemplate,
                    field_width_in_rem_for_process_dashboard: e.target.value,
                  })
                }
                label={`Process Dashboard Width (REM)`}
              />
            )}
            <Box
              style={{
                display: 'flex',
                columnGap: 16,
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setDialogOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button color="primary" variant="contained" onClick={saveChanges}>
                Save
              </Button>
            </Box>
          </Box>
        </TabPanel>
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2 }}>
          <ConditionalEnableDisableUI
            field={fieldTemplate}
            activityTemplateId={activityTemplateId}
          />

          {fieldTemplate.is_subfield == 'yes' ? (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <TableFieldConstraintUI
                field={fieldTemplate}
                ftData={ftData}
                activityTemplateId={activityTemplateId}
                processTemplateId={processTemplateId}
              />

              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <FieldConstraintUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          ) : (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <FieldConstraintUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          )}

          {fieldTemplate.is_subfield == 'yes' ? (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <AggregationTables field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          ) : null}

          {(fieldTemplate?.data_structure == FIELD_TYPES.LIST ||
            fieldTemplate?.data_structure == FIELD_TYPES.LIST_MULTI) && (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <ParentFieldSetupUI field={fieldTemplate} activityTemplateId={activityTemplateId} />
            </>
          )}

          <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
          <FieldVisibilityProtectionUI field={fieldTemplate} />

          {fieldTemplate?.is_pivot_field == 'yes' && (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <PivotFieldFilterSetup
                fieldTemplate={fieldTemplate}
                activityTemplateId={activityTemplateId}
                importedFieldList={importedFieldList}
              />
            </>
          )}

          {fieldTemplate?.is_pivot_field == 'yes' && (
            <>
              <Divider direction="horizontal" style={{ margin: '16px 0px' }} />
              <PreventProcessCreationUI
                fieldTemplate={fieldTemplate}
                processTemplateId={processTemplateId}
                activityTemplateId={activityTemplateId}
              />
            </>
          )}
        </TabPanel>
        <TabPanel value="3" sx={{ padding: 0, paddingTop: 2 }}>
          <FieldComputationSetupUI
            field={onlyFieldColumns(transformFieldPropForAPI(fieldTemplate), allColumns)}
            setFieldTemplate={setFieldTemplate}
            activityTemplateId={activityTemplateId}
            processTemplateId={processTemplateId}
            saveChanges={saveComputationChanges}
          />
        </TabPanel>
        <TabPanel value="4" sx={{ padding: 0, paddingTop: 2 }}>
          <RecurringTableSetup
            fieldTemplate={fieldTemplate}
            derivableFieldTemplates={derivableFieldTemplates}
          />
        </TabPanel>
      </TabContext>
    </DialogWrapper>
  )
}

export default EditFieldTemplateDialog

/* id, field_template_name, data_structure, 
"sequence", description, created_at, modified_at, 
created_by, modified_by, list_id, page_template_id, 
is_subfield, is_required, metadata, is_assignment_field, 
is_read_only, is_computed, is_hidden_in_process_dashboard, 
is_derived_field, derived_from_field_template_id, 
is_selectable_table, allow_add_rows, allow_delete_rows, 
pivot_field_template_id, is_imported_field, 
imported_from_field_template_id, is_hidden, is_pivot_field, 
import_from_process_template_id, subfield_column_width_in_rem, 
field_width_in_rem_for_process_dashboard, is_one_time_input, 
dynamic_field_type, is_process_counter, num_decimal_places, 
is_lrp_display, default_value */
